import { icon } from './icons.js';
import { emit, clamp } from '../core/utils.js';
import { parseYouTubeURL } from '../core/youtube.js';
import { createYouTubeTransport } from '../services/youtube.js';
import { audioSession } from '../services/audio-session.js';

export class VideoPlayer extends HTMLElement {
  connectedCallback() {
    audioSession.sync();
    if (this.video) { this.startFrames(); return; }
    this.innerHTML = `<div class="video-stage">
      <video class="tutorial-video" playsinline preload="metadata" aria-label="Piano tutorial"></video>
      <div class="youtube-container" hidden></div>
      <div class="player-empty">
        <div class="stage-grid"></div><div class="stage-glow"></div>
        <span class="stage-eyebrow">A SPACE TO FIND YOUR RHYTHM</span>
        <div class="piano-art" aria-hidden="true"><div class="falling-note n1"></div><div class="falling-note n2"></div><div class="falling-note n3"></div><div class="falling-note n4"></div><div class="keyboard-art">${Array.from({length: 15}, (_, i) => `<i class="white-key ${[3, 7, 10].includes(i) ? 'lit' : ''}">${![2, 6, 9, 13, 14].includes(i) ? '<b></b>' : ''}</i>`).join('')}</div></div>
        <h2>Good things start with a few notes.</h2><p>Find a tune in the library, or start your own project.</p>
        <button class="button accent" data-action="catalog">${icon('library')} Browse the library</button>
        <button class="text-button demo-link" data-action="demo">${icon('shuffle')} Try a random tune ${icon('chevron')}</button>
      </div>
      <div class="video-loading" hidden><span class="spinner"></span><span>Opening your video…</span></div>
      <div class="countdown-overlay" hidden><span></span><small>Get your hands ready</small><button class="button ghost" data-action="cancel-countdown">Cancel</button></div>
      <div class="video-drop-overlay" hidden>${icon('folder')} Drop your tutorial here</div>
    </div>`;
    this.video = this.querySelector('video'); this.emptyMarkup = this.querySelector('.player-empty').innerHTML; this.generation = 0; this.loaded = false; this._sourceKind = 'local';
    this.addEventListener('click', event => { const action = event.target.closest('[data-action]')?.dataset.action; if (action) emit(this, 'command', { type: action }); });
    for (const name of ['play', 'playing', 'pause', 'waiting', 'stalled', 'timeupdate', 'durationchange', 'ratechange', 'volumechange', 'seeking', 'seeked', 'ended']) {
      this.video.addEventListener(name, () => { if (this.sourceKind === 'local') emit(this, 'player-state', { event: name, ...this.state }); });
    }
    this.video.addEventListener('error', () => { if (this.sourceKind === 'local' && this.loaded) emit(this, 'player-error', { message: 'This video could not be decoded. Convert it to MP4 with the Mac conversion tool.' }); });
    this.video.addEventListener('click', event => {
      if (event.detail > 1) { clearTimeout(this.clickTimer); return; }
      this.clickPaused = this.state.paused; this.clickToggled = false;
      clearTimeout(this.clickTimer);
      this.clickTimer = setTimeout(() => { this.clickToggled = true; emit(this, 'command', {type:'toggle'}); }, 300);
    });
    this.video.addEventListener('dblclick', event => {
      event.preventDefault(); clearTimeout(this.clickTimer);
      if (!this.loaded) return;
      // Preserve playback even when the system double-click interval exceeds our click delay.
      if (this.clickToggled) emit(this, 'command', {type:this.clickPaused ? 'pause' : 'play'});
      this.clickToggled = false;
      emit(this, 'command', {type:'fullscreen'});
    });
    this.startFrames();
  }
  get sourceKind() { return this._sourceKind ?? 'local'; }
  get state() {
    if (this.sourceKind === 'youtube' && this.youtube) return this.youtube.state;
    const v = this.video; return { time: v?.currentTime || 0, duration: Number.isFinite(v?.duration) ? v.duration : 0, paused: v?.paused ?? true, rate: v?.playbackRate || 1, volume: v?.volume ?? 1, muted: v?.muted || false, seeking: v?.seeking || false };
  }
  destroyYouTube() {
    clearInterval(this.youtubeTimer); this.youtubeAbort?.abort(); this.youtubeAbort = null;
    this.youtube?.destroy(); this.youtube = null; this._sourceKind = 'local'; this.mediaInfo = null;
    const container = this.querySelector('.youtube-container'); if (container) { container.replaceChildren(); container.hidden = true; }
    if (this.video) this.video.hidden = false;
  }
  async load(file) {
    clearTimeout(this.clickTimer);
    const generation = ++this.generation;
    this.cancelLoad?.(); this.destroyYouTube(); this.loaded = false; this.video.pause(); this.showCountdown(null);
    if (this.url) URL.revokeObjectURL(this.url);
    this.url = URL.createObjectURL(file);
    this.querySelector('.video-loading').hidden = false;
    this.video.src = this.url;
    try {
      await new Promise((resolve, reject) => {
        const cleanup = () => { clearTimeout(timer); this.video.removeEventListener('loadedmetadata', ready); this.video.removeEventListener('error', failed); if (this.cancelLoad === cancel) this.cancelLoad = null; };
        const ready = () => { cleanup(); Number.isFinite(this.video.duration) && this.video.duration > 0 ? resolve() : reject(new Error('This file has no playable video duration.')); };
        const failed = () => { cleanup(); reject(new Error('Your browser cannot play this file. Convert it to H.264/AAC MP4 with the Mac tool.')); };
        const cancel = () => { cleanup(); reject(new DOMException('A newer video was opened.', 'AbortError')); };
        const timer = setTimeout(() => { cleanup(); reject(new Error('The video took too long to open. Try a local copy or convert it to MP4.')); }, 20000);
        this.cancelLoad = cancel; this.video.addEventListener('loadedmetadata', ready); this.video.addEventListener('error', failed); this.video.load();
      });
      if (generation !== this.generation) throw new DOMException('Cancelled', 'AbortError');
      this.loaded = true; this.classList.add('has-video'); this.querySelector('.player-empty').hidden = true;
      return this.video.duration;
    } catch (error) {
      if (generation === this.generation) { this.classList.remove('has-video'); this.querySelector('.player-empty').hidden = false; this.video.removeAttribute('src'); this.video.load(); URL.revokeObjectURL(this.url); this.url = null; }
      throw error;
    } finally { if (generation === this.generation) this.querySelector('.video-loading').hidden = true; }
  }
  async loadYouTube(url) {
    const info = parseYouTubeURL(url);
    this.clear(); const generation = ++this.generation;
    const controller = new AbortController(); this.youtubeAbort = controller;
    this._sourceKind = 'youtube'; this.video.hidden = true;
    const container = this.querySelector('.youtube-container'); container.hidden = false;
    const loading = this.querySelector('.video-loading'); loading.hidden = false;
    this.querySelector('.player-empty').hidden = true; this.classList.add('has-video');
    try {
      const transport = await createYouTubeTransport(container, info, {
        signal: controller.signal,
        onReady: () => { if (generation === this.generation) loading.hidden = true; },
        onState: event => { if (generation === this.generation && this.loaded) emit(this, 'player-state', { event, ...this.state }); },
        onError: error => {
          if (generation !== this.generation) return;
          if (!this.loaded) this.youtubeLoadError = error;
          else emit(this, 'player-error', { message: error.message });
        },
      });
      if (generation !== this.generation || controller.signal.aborted) { transport.destroy(); throw new DOMException('Cancelled', 'AbortError'); }
      this.youtube = transport;
      await new Promise((resolve, reject) => {
        const cleanup = () => { clearInterval(poll); clearTimeout(timeout); controller.signal.removeEventListener('abort', cancel); };
        const cancel = () => { cleanup(); reject(new DOMException('A newer video was opened.', 'AbortError')); };
        const check = () => {
          if (this.youtubeLoadError) { const error = this.youtubeLoadError; this.youtubeLoadError = null; cleanup(); reject(error); return; }
          if (this.youtube?.state.duration > 0) { cleanup(); resolve(); }
        };
        const poll = setInterval(check, 100);
        const timeout = setTimeout(() => { cleanup(); reject(new Error('YouTube did not provide a playable duration. Open the link again and press Play inside the video if prompted.')); }, 25000);
        controller.signal.addEventListener('abort', cancel, { once: true });
        check();
      });
      if (generation !== this.generation) throw new DOMException('Cancelled', 'AbortError');
      const videoData = this.youtube.player.getVideoData?.() ?? {};
      if (videoData.isLive) throw new Error('Live YouTube streams do not have stable practice timestamps. Choose a recorded video.');
      this.loaded = true;
      this.mediaInfo = { ...info, title: typeof videoData.title === 'string' && videoData.title.trim() ? videoData.title.slice(0, 500) : `YouTube tutorial ${info.id}` };
      let previous = '';
      this.youtubeTimer = setInterval(() => {
        if (!this.isConnected || !this.loaded || generation !== this.generation) return;
        const state = this.state, signature = [state.time, state.paused, state.rate, state.muted, state.volume, state.duration].join(':');
        if (signature !== previous) { previous = signature; emit(this, 'player-state', { event: 'timeupdate', ...state }); }
        if (!state.paused) emit(this, 'player-frame', state);
      }, 100);
      return this.state.duration;
    } catch (error) {
      if (generation === this.generation) {
        this.destroyYouTube(); this.loaded = false; this.classList.remove('has-video'); this.querySelector('.player-empty').hidden = false;
      }
      throw error;
    } finally { if (generation === this.generation) loading.hidden = true; }
  }
  play() { audioSession.sync(); return !this.loaded ? Promise.resolve() : this.sourceKind === 'youtube' ? this.youtube.play() : this.video.play(); }
  clear() {
    clearTimeout(this.clickTimer); ++this.generation; this.cancelLoad?.(); this.destroyYouTube(); this.youtubeLoadError = null; this.loaded = false; this.video.pause();
    this.video.removeAttribute('src'); this.video.load();
    if (this.url) URL.revokeObjectURL(this.url); this.url = null;
    this.classList.remove('has-video'); this.querySelector('.player-empty').hidden = false;
    this.querySelector('.video-loading').hidden = true; this.showCountdown(null);
  }
  pause() { if (this.sourceKind === 'youtube') this.youtube?.pause(); else this.video.pause(); }
  seek(time) { if (this.loaded) { if (this.sourceKind === 'youtube') this.youtube.seek(time); else this.video.currentTime = clamp(time, 0, this.video.duration); } }
  setRate(rate) { if (this.sourceKind === 'youtube') return this.youtube?.setRate(clamp(rate, .25, 2)); this.video.playbackRate = clamp(rate, .25, 2); this.video.preservesPitch = true; return this.video.playbackRate; }
  setMuted(muted) { if (!muted) audioSession.sync(); if (this.sourceKind === 'youtube') this.youtube?.setMuted(muted); else this.video.muted = muted; }
  showCountdown(number) { const overlay = this.querySelector('.countdown-overlay'); overlay.hidden = number === null; overlay.querySelector('span').textContent = number ?? ''; }
  startFrames() {
    const frame = () => {
      if (!this.isConnected) return;
      if (this.sourceKind === 'local' && this.loaded && !this.video.paused) emit(this, 'player-frame', this.state);
      // The media clock must also drive music when its local video pane is hidden.
      this.frameID = requestAnimationFrame(frame);
    };
    frame();
  }
  disconnectedCallback() { clearTimeout(this.clickTimer); ++this.generation; this.cancelLoad?.(); this.destroyYouTube(); this.video?.pause(); if (this.url) URL.revokeObjectURL(this.url); cancelAnimationFrame(this.frameID); }
}
customElements.define('mt-video-player', VideoPlayer);
